const en = {
  code: "en",
  htmlLang: "en",
  meta: {
    title: "Give Your Family Clarity | Will & Legacy Planning Webinar",
    description:
      "Join a complimentary Singapore-focused webinar on Wills, CPF nominations, executor and beneficiary roles, asset organisation and the practical steps behind a clearer legacy plan.",
  },
  brand: { name: "AFFINITY", small: "Representing Infinity Financial Advisory" },
  nav: { context: "Complimentary · Live online webinar on Zoom · Singapore-focused", reserve: "Reserve my seat", langLabel: "Choose page language" },
  hero: {
    chip: "{count} live Zoom webinars · English {en} · 中文 {zh} · 10–11 AM SGT",
    title: ["Your family may inherit everything.", "Except the answers."],
    lead:
      "Learn how Wills, CPF nominations, executor and beneficiary roles, and a clear asset roadmap can work together, so the people you love are not left guessing when it matters most.",
    benefits: ["Know what each document actually does", "Spot the gaps families often overlook", "Leave with a clear next-step checklist"],
    quickLabel: "Email address",
    quickPlaceholder: "Enter your email to begin",
    quickButton: "Start My Registration",
    learnLink: "See exactly what you’ll learn ↓",
    micros: ["Complimentary", "100% online via Zoom, no travel needed", "No obligation"],
    formatNote: "Every session is a live online webinar on Zoom. Join from home on your phone, tablet or computer. This is not a physical event.",
    presenterInitials: "NL",
    presenter: "Led by Nicholas Lee · Senior Associate Director (Financial Services), Infinity Financial Advisory",
  },
  reflection: {
    kicker: "A question worth answering",
    title: "If your family had to handle everything tomorrow, would they know where to begin?",
    options: ["Yes, confidently", "I’m not completely sure"],
    prompt: "Choose the answer that feels most honest today.",
    answers: [
      "That is a strong start. The session will help you test whether the separate pieces still work together.",
      "That uncertainty is exactly what this session helps you turn into a practical next step.",
    ],
    link: "Turn uncertainty into a clear next step →",
  },
  credibility: [
    { icon: "SG", text: "Singapore-specific planning considerations" },
    { icon: "1H", text: "One focused hour on Zoom, explained without jargon" },
    { icon: "Q", text: "Practical examples with time for live Q&A" },
  ],
  marquee: ["Wills", "CPF Nominations", "Executors & Beneficiaries", "Guardianship", "Asset Inventory", "Life-stage Reviews", "Singapore-focused", "Complimentary Webinar"],
  clarity: {
    eyebrow: "The real legacy test",
    title: "Could the people you love find the answers without you?",
    lead: "Estate planning is not just about what you leave behind. It is whether the right people can find it, understand it and act when they need to.",
    button: "Build a Clearer Plan",
    mapKicker: "Tap each checkpoint",
    mapTitle: "Four questions your family should not have to guess.",
    interactive: "Interactive",
    maps: [
      { title: "What do you own?", meta: "Assets · policies · records", message: "Would they know where your important assets, policies and records are kept?", question: "assets" },
      { title: "Who should act?", meta: "Executor · beneficiary · adviser", message: "Would they know who you trust to coordinate the next steps and seek professional guidance?", question: "roles" },
      { title: "What did you intend?", meta: "Will · nominations · wishes", message: "Would valid instructions and nominations exist, not only verbal assumptions?", question: "will" },
      { title: "Is it still current?", meta: "Marriage · children · assets", message: "Would your arrangements still reflect your wishes after major changes in your life or family?", question: "review" },
    ],
  },
  plan: {
    eyebrow: "The whole picture",
    title: "A Will is important. But it is not the whole plan.",
    lead: "A clearer legacy plan connects several decisions. Explore each piece to see why relying on a single document can leave important gaps.",
    tabs: [
      {
        label: "Will", panelLabel: "Your estate", title: "Who receives what, and who carries out your instructions?",
        copy: "Without a valid Will, eligible family members generally need to apply for Letters of Administration, and the law determines how a non-Muslim estate is distributed. A properly prepared Will can name beneficiaries and an executor.",
        source: "Official reference: My Legacy", href: "https://mylegacy.life.gov.sg/end-of-life-planning/write-a-will/if-you-dont-have-a-will/",
        prompt: "Has your Will kept pace with marriage, children, property purchases or changing relationships?",
      },
      {
        label: "CPF", panelLabel: "Your CPF savings", title: "Your Will does not control your CPF savings.",
        copy: "CPF savings do not form part of your estate and cannot be included in a Will. A CPF nomination lets you specify who should receive your CPF savings and in what proportion.",
        source: "Official reference: CPF Board", href: "https://www.cpf.gov.sg/member/account-services/providing-for-your-loved-ones/making-a-cpf-nomination",
        prompt: "Is your nomination current, especially after major family changes?",
      },
      {
        label: "Review", panelLabel: "Your review cycle", title: "Does your plan still reflect the life you have today?",
        copy: "Marriage, divorce, children, property purchases, business changes and shifting relationships can affect whether older instructions still match your intentions. A legacy plan should be reviewed after major life changes.",
        source: "Official planning resources: My Legacy", href: "https://mylegacy.life.gov.sg/",
        prompt: "When did you last review your Will, nominations and key contact details?",
      },
      {
        label: "Assets", panelLabel: "Your information", title: "Could your family locate what you own?",
        copy: "A plan is harder to carry out when accounts, policies, properties, liabilities and important documents are scattered or unknown. A maintained asset inventory helps the right people know where to begin.",
        source: "Official planning resources: My Legacy", href: "https://mylegacy.life.gov.sg/",
        prompt: "Does someone you trust know how to find your latest records without exposing sensitive passwords?",
      },
      {
        label: "People", panelLabel: "Your trusted circle", title: "The right documents still need the right people.",
        copy: "Executors, guardians, nominees and beneficiaries have different roles. Choosing thoughtfully, and helping them understand what may be expected, can reduce uncertainty later.",
        source: "Official reference: Singapore Courts", href: "https://www.judiciary.gov.sg/family/probate-and-administration",
        prompt: "Are the people you named still willing, suitable and contactable?",
      },
    ],
    askYourself: "Ask yourself:",
  },
  beliefs: {
    eyebrow: "Common assumptions",
    title: "Which of these sounds most like you?",
    lead: "Tap a statement. If it feels familiar, this webinar was designed to help you turn an assumption into a clearer next step.",
    assumption: "ASSUMPTION",
    items: [
      { short: "“I’m still young.”", title: "“I’m still young. I can settle this later.”", copy: "Legacy planning is not only about age. It is about whether someone can act clearly if life changes suddenly, and whether the people you care about know what to do." },
      { short: "“I already have a Will.”", title: "“I already have a Will, so I’m covered.”", copy: "A Will matters, but it does not cover every asset or every practical detail. CPF nominations, ownership arrangements, executor responsibilities and an up-to-date asset inventory may each play a different role." },
      { short: "“My family knows.”", title: "“My spouse knows what I want.”", copy: "A conversation is valuable, but loved ones may still need valid documents, authority and accurate information to carry out those wishes when the time comes." },
      { short: "“I don’t own that much.”", title: "“I don’t own enough to need a plan.”", copy: "Legacy planning is not reserved for the wealthy. Even a modest estate can involve CPF savings, insurance, a home, digital accounts, personal wishes, executor responsibilities and practical family arrangements." },
    ],
  },
  journey: {
    eyebrow: "Your clarity journey",
    title: "Four conversations. One clearer picture.",
    lead: "The session is structured to move from “I know I should do something” to “I understand what to review next.”",
    duration: "Live Zoom webinar · 60 minutes · fully online",
    takeaway: "Your takeaway",
    steps: [
      { title: "See the gaps", copy: "Understand how Wills, nominations, executor roles and asset organisation work together.", outcome: "Turn scattered documents into one coordinated picture.", detail: "See how Wills, nominations, executor roles and organised asset information connect, then identify the piece your family may be missing." },
      { title: "Know the roles", copy: "Clarify the practical differences between an executor, guardian, nominee and beneficiary.", outcome: "Choose the right people for the right responsibilities.", detail: "Separate the responsibilities of an executor, guardian, nominee and beneficiary so each person knows what they can and cannot do." },
      { title: "Avoid assumptions", copy: "Learn where “my family will know” or “my Will covers everything” can create blind spots.", outcome: "Replace assumptions with clear, findable instructions.", detail: "Pressure-test familiar beliefs such as ‘my Will covers everything’ or ‘my family already knows’ before they become stressful surprises." },
      { title: "Choose next steps", copy: "Leave with a simple checklist of conversations, documents and professional guidance to consider.", outcome: "Leave knowing exactly what to review first.", detail: "Use a manageable review checklist to decide which conversations, documents and qualified professionals may deserve your attention next." },
    ],
  },
  video: {
    eyebrow: "A message worth hearing",
    title: "This is not about planning for death.",
    lead: "It is about making life easier for the people who may one day need to carry out your wishes.",
    note: "Highlights and testimonials from our past physical seminars. The webinar covers the same content.",
    points: [
      "Why a collection of documents is not necessarily a coordinated plan",
      "The questions families often discover only when it is too late to ask",
      "How to begin without feeling overwhelmed by legal or financial terminology",
    ],
    button: "Join the Next Live Session",
    caption: "Will & Legacy Planning · Past physical seminar highlights",
    quotesTitle: "What past attendees said",
    quotes: [
      { text: "I walked in thinking a Will was enough. I left understanding how CPF nominations, insurance and the Will all have to work together.", who: "Attendee · April 2025 physical seminar" },
      { text: "Clear, practical and free of jargon. The roles of executor, guardian and nominee were finally explained in a way my whole family could follow.", who: "Attendee · November 2025 physical seminar" },
      { text: "The most useful two hours we have spent on this topic. We now know exactly which conversation to have first, and with whom.", who: "Attendee · Physical seminar" },
    ],
  },
  readiness: {
    eyebrow: "60-second reflection",
    title: "How clear is your current plan?",
    lead: "Tick every statement that is confidently true today. This is a reflection tool, not a legal or financial assessment.",
    checks: [
      "My Will reflects my current family and wishes.",
      "My CPF nomination is current.",
      "I have chosen a suitable executor and discussed the role with them.",
      "My trusted person can locate my important records.",
      "The people I appointed understand their roles.",
    ],
    confident: "Confidently true",
    scoreOf: "of 5 clear",
    scoreNote: "Educational reflection only · no data is saved",
    button: "Reserve My Webinar Seat",
    scores: [
      ["There is room for more clarity.", "You do not need every answer before attending. The webinar is designed to help you identify the questions worth asking."],
      ["A useful first step.", "You have started thinking about the plan. The session can help you see which connected pieces may still deserve attention."],
      ["Some foundations are in place.", "You may benefit from checking whether your arrangements still match your current family, assets and wishes."],
      ["You are building clarity.", "Your foundation looks more organised. The webinar can help you review how the different pieces work together."],
      ["A strong level of preparation.", "Even well-prepared plans deserve a review after major life changes or when roles and relationships evolve."],
      ["You have considered the key pieces.", "Use the webinar as a practical review and bring any questions about keeping your arrangements current."],
    ],
  },
  speaker: {
    eyebrow: "Your speaker",
    title: "Complex decisions, explained clearly.",
    lead: "A focused, practical session led by someone who understands how to make important planning concepts easier to act on.",
    sessionSuffix: { one: " session", many: " sessions" },
    people: [
      {
        id: "nicholas",
        tag: "English Webinar Speaker",
        name: "Nicholas Lee",
        role: "Senior Associate Director (Financial Services) · Infinity Financial Advisory",
        rnf: "RNF: LCW300188187",
        initials: "NL",
        portrait: "/speaker-nicholas.jpg",
        portraitFit: "portrait-cover",
        bio: "With more than 12 years of experience, Nicholas Lee has built a distinguished career helping individuals and families navigate important financial decisions with greater confidence. A Certified Financial Planner and consistent Million Dollar Round Table (MDRT) member from 2018 to 2026, he is known for turning complex financial concepts into thoughtful, personalised strategies that strengthen financial confidence and family harmony. His commitment to financial literacy has brought him to platforms including NUS and the media, where he shares practical insights with wider audiences. At the heart of his work is a clear purpose: helping families build enduring financial foundations that can support generations to come.",
        focus: ["Will planning", "CPF nominations", "Executor & beneficiary roles", "Asset organisation"],
      },
    ],
  },
  mid: { title: "The best time to organise your wishes is while every choice is still yours.", button: "Save My Complimentary Webinar Seat" },
  sessions: {
    eyebrow: "Choose your webinar session",
    title: "Pick the date and language that suits you.",
    lead: "Every session is complimentary, live on Zoom and runs for one focused hour (10:00–11:00 AM SGT). English sessions are led by Nicholas Lee; 中文 sessions are led by Vincent Chua 蔡镇安 (19 Sep) and Fion Yong 杨炎芬 (21 Nov).",
    englishGroup: "English · Nicholas Lee",
    chineseGroup: "中文 · Vincent Chua 蔡镇安 & Fion Yong 杨炎芬",
    time: "10:00 AM – 11:00 AM (SGT)",
    reserve: "Reserve this webinar session",
    format: "Live online webinar via Zoom · join from anywhere",
  },
  faq: {
    eyebrow: "Questions before you join?",
    title: "Start here.",
    lead: "If your question is not covered, reserve your seat and bring it to the live Q&A.",
    items: [
      { q: "Is this a physical seminar or an online webinar?", a: "It is a 100% online webinar held live on Zoom. There is no physical venue and nothing to travel to. Join from home or anywhere in Singapore on your phone, tablet or computer. Your personal Zoom joining link is emailed to you after registration and again the day before the session." },
      { q: "Is this webinar only for older adults?", a: "No. Legacy planning becomes relevant when you have people, responsibilities, assets or decisions you care about. Many attendees begin after marriage, having children, buying a home or taking on caregiving responsibilities." },
      { q: "I already have a Will. Will this still be useful?", a: "Yes. The session looks beyond the Will to related areas such as CPF nominations, executor and beneficiary roles, asset information and the need to review arrangements after major life changes." },
      { q: "Will I receive personal legal or financial advice?", a: "No. The webinar provides general educational information. Your circumstances may require independent financial, legal, tax or other professional advice before you act." },
      { q: "Do I need any prior knowledge?", a: "No. The session is designed for everyday Singapore residents and uses clear, practical language. You can also submit your main question when registering." },
      { q: "How will I receive the webinar access details?", a: "After registration, the confirmation and joining instructions will be sent to the email address or mobile number you provide. Check your spam or promotions folder if you do not see it." },
    ],
  },
  register: {
    eyebrow: "Reserve your webinar seat",
    title: ["Give your family something more valuable than assets: ", "clarity."],
    lead: "Join the next complimentary live webinar and take the first step towards a plan your loved ones can understand and carry out.",
    chooseDate: "Choose date",
    dateLabel: "Date",
    timeLabel: "Time",
    venueLabel: "Venue",
    time: "10:00 AM – 11:00 AM (SGT)",
    venue: "Live online via Zoom (not a physical event)",
    speakerLabel: "Speaker",
    langTag: { English: "English", Chinese: "中文" },
    countdown: ["Days", "Hours", "Minutes", "Seconds"],
    steps: ["Reserve your seat", "Personalise"],
    labels: { name: "Full Name", email: "Email Address", mobile: "Mobile Number", session: "Choose your session", question: "Your main question about Will & Legacy Planning", invitedBy: "Invited by", ageRange: "Your age range" },
    ageRanges: { under30: "Under 30", "30s": "30 – 39", "40s": "40 – 49", "50s": "50 – 59", "60plus": "60 and above" },
    placeholders: { name: "e.g. Tan Wei Ming", email: "you@example.com", mobile: "8123 4567", other: "Tap a topic above or type your own question", invitedBy: "Name of the person who invited you" },
    mobilePrefix: "+65",
    optional: "(optional)",
    invitedByLocked: "Filled in from your adviser’s invitation link.",
    continue: "Continue",
    back: "Back",
    privacyNote: "By submitting this form, you consent to being contacted regarding your registration and event updates.",
    questions: {
      will: "Writing or updating my Will",
      cpf: "Making or reviewing my CPF nomination",
      roles: "Executor, guardian & beneficiary roles",
      assets: "Organising my assets & important records",
      review: "Checking whether my current plan is complete",
      unsure: "I’m not sure where my legacy-planning gaps are",
      other: "Something else",
    },
    consent: "I have read the notice above and consent to the collection and use of my personal data for the purposes stated.",
    pdpa: {
      intro: "By providing us your personal data (i.e. name, email address and phone number), you acknowledge and consent to our collection of your personal data for the purposes listed below:",
      purposes: [
        "Sending you marketing, advertising or promotional materials related to the content of this website, whether by call, text or email;",
        "Provision of products & services which you have requested for.",
      ],
      withdraw: "Please note that you are entitled to withdraw your consent for the collection of your personal data at any point in time by providing a notification to ",
      email: "marketing@affinitygroup.sg",
    },
    submit: "Reserve My Webinar Seat",
    reassurance: "Complimentary · No obligation · Your details are kept confidential",
    messages: {
      required: "Please complete the required fields before continuing.",
      mobile: "Please enter a valid 8-digit Singapore mobile number.",
      email: "Please enter a valid email address.",
      consent: "Please tick the consent box to continue.",
      ageRange: "Please select your age range.",
      submitting: "Reserving your seat…",
      success: "Your seat is reserved. Please check your inbox for the confirmation details.",
      duplicate: "This email is already registered for the selected session. We’ll see you there!",
      failed: "We could not complete the registration. Please try again or contact the Affinity Group team.",
    },
    successHeading: "Your seat is reserved.",
    successMeta: "A confirmation email is on its way. Zoom joining details follow closer to the session.",
    another: "Register another person",
    share: {
      kicker: "Know someone who should hear this?",
      button: "Share via WhatsApp",
      message: "Hi! I've just reserved a seat for a complimentary Will & Legacy Planning webinar by Affinity Group on {date}, {time}, live on Zoom. Thought it might be useful for you too. Reserve your seat here: {url}",
    },
    calendar: {
      heading: "Add to your calendar",
      title: "Will & Legacy Planning Webinar · Affinity Group",
      details: "Your seat is reserved for Affinity Group's complimentary Will & Legacy Planning webinar (10:00 AM – 11:00 AM SGT, live on Zoom). Your Zoom joining link will be emailed before the session.",
      google: "Google",
      apple: "Apple / iCal",
      outlook: "Outlook",
    },
  },
  footer: {
    links: ["Privacy Policy", "Terms of Use", "Frequently Asked Questions"],
    blurb: "Helping individuals and families better understand their protection, long-term care and financial planning needs.",
    connect: "Connect With Us",
    quickLinksTitle: "Quick Links",
    quickLinks: [
      { label: "What You Will Learn", href: "#learn" },
      { label: "Watch", href: "#watch" },
      { label: "Speaker", href: "#speakers" },
      { label: "FAQ", href: "#faq" },
      { label: "Reserve a Seat", href: "#register" },
    ],
    whatsappMessage: "Hi, I'm enquiring about the upcoming Will & Legacy Planning Webinar.",
    disclaimer: "Affinity Group is a group of financial adviser representatives representing Infinity Financial Advisory Pte. Ltd. This webinar and website are for general information and educational purposes only. Nothing presented constitutes financial, legal, tax, accounting or estate-planning advice, or an offer or recommendation of any financial product. Information may not be complete or suitable for your circumstances. Please seek advice from appropriately qualified professionals before making any decision. Registration is subject to availability and confirmation.",
    rights: "Affinity Group. All rights reserved.",
  },
  sticky: { mobileTitle: "Complimentary live webinar", desktopKicker: "Next webinar", reserve: "Reserve Webinar Seat", reserveLong: "Reserve My Webinar Seat" },
};

export default en;
